import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import CustomPagination from "../../components/CustomPagination";
import { ReactComponent as Edit } from "../../assets/images/edit.svg";
import Modal from "../../components/Modal";
import { formatDate } from "../../utils/DateFormat";
import style from "./style";

const UniversalTaxPage = ({
  country,
  data,
  label,
  handleEditTaxRule,
  handleAddTaxRule,
  handleDeleteTaxRule,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  deleteTaxRule,
  totalPage,
  totalCount,
}) => {
  const [open, setOpen] = useState(false);
  const [payer, setPayer] = useState();
  const [isEdit, setIsEdit] = useState(false);
  const [datefrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [applyTo, setApplyTo] = useState("");
  const [employerValue, setEmployerValue] = useState("");
  const [employeeValue, setEmployeeValue] = useState("");
  const [description, setDescription] = useState();
  const [opens, setOpens] = useState(false);
  const [type, setType] = useState();
  const [payType, setPayType] = useState("");
  const [value, setValue] = useState();
  const [id, setID] = useState();
  const [cap, setCap] = useState();
  const [originalValues, setOriginalValues] = useState(null);
  const role = localStorage.getItem("role");
  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditTaxTable = role === "SuperAdmin" || permissions?.taxTable?.edit === true;
  useEffect(() => {
    if (payer === "Employee") {
      setEmployerValue("");
    }
    if (payer === "Employer") {
      setEmployeeValue("");
    }
  }, [payer]);

  useEffect(() => {
    if (type !== "Percentage") return;
    // Ensure percentage values don't exceed 100
    if (employerValue !== "" && Number(employerValue) > 100) {
      setEmployerValue("100");
    }
    if (employeeValue !== "" && Number(employeeValue) > 100) {
      setEmployeeValue("100");
    }
  }, [type, employerValue, employeeValue]);

  const valueIsValid = useMemo(() => {
    if (payer === "Both") {
      return employerValue !== "" && employeeValue !== "";
    }
    if (payer === "Employer") {
      return employerValue !== "";
    }
    if (payer === "Employee") {
      return employeeValue !== "";
    }
    return false;
  }, [payer, employerValue, employeeValue]);

  const hasChanges = useMemo(() => {
    if (!isEdit || !originalValues) return true;

    const formatDateForCompare = (date) => {
      if (!date) return "";
      try {
        return dayjs(date).format("YYYY-MM-DD");
      } catch (e) {
        return "";
      }
    };

    // Normalize values for comparison
    const normalizeValue = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    };

    const normalizeString = (val) => {
      return val === null || val === undefined ? "" : String(val);
    };

    // Compare basic fields
    if (payer !== originalValues.paidBy) return true;
    if (type !== originalValues.type) return true;
    if ((payType || "") !== (originalValues.payType || "")) return true;
    if (formatDateForCompare(datefrom) !== formatDateForCompare(originalValues.startDate)) return true;
    if (formatDateForCompare(dateTo) !== formatDateForCompare(originalValues.endDate)) return true;
    if ((applyTo === "All Employees") !== originalValues.forAllEmp) return true;
    if (normalizeString(description) !== normalizeString(originalValues.description)) return true;
    if (normalizeValue(cap) !== normalizeValue(originalValues.maxCap)) return true;

    // Compare values based on paidBy field
    if (payer === "Both") {
      if (normalizeValue(employerValue) !== normalizeValue(originalValues.employerValue)) return true;
      if (normalizeValue(employeeValue) !== normalizeValue(originalValues.employeeValue)) return true;
    } else if (payer === "Employer") {
      if (normalizeValue(employerValue) !== normalizeValue(originalValues.employerValue)) return true;
    } else if (payer === "Employee") {
      if (normalizeValue(employeeValue) !== normalizeValue(originalValues.employeeValue)) return true;
    }

    return false;
  }, [isEdit, originalValues, payer, type, payType, datefrom, dateTo, applyTo, employerValue, employeeValue, description, cap]);

  const buildTaxRulePayload = () => {
    const formatDate = (date) =>
      date ? dayjs(date).format("YYYY-MM-DD") : undefined;

    const payload = {
      taxLabelId: label?._id,
      country: label?.country,
      taxName: label?.title,
      type,
      payType: payType || undefined,
      paidBy: payer,
      description,
      startDate: formatDate(datefrom),
      endDate: formatDate(dateTo),
      forAllEmp: applyTo === "All Employees",
    };

    if (payer === "Employee" || payer === "Both") {
      payload.employeeValue = parseFloat(employeeValue);
    }

    if (payer === "Employer" || payer === "Both") {
      payload.employerValue = parseFloat(employerValue);
    }

    if (cap && cap !== "") {
      payload.maxCap = parseFloat(cap);
    }

    return payload;
  };

  const handleNumericChange = (
    setter,
    { min = 0, enforcePercentageLimit = false } = {}
  ) => (event) => {
    let inputValue = event.target.value;

    if (inputValue === "") {
      setter("");
      return;
    }

    inputValue = inputValue.replace(/[^0-9.]/g, "");

    const parts = inputValue.split(".");
    if (parts.length > 2) return;

    // Allow in-progress decimal typing: "0." or "."
    if (inputValue === "." || inputValue === "0.") {
      setter(inputValue);
      return;
    }

    // Strip leading zeros: "007" → "7", "000" → "0", but keep "0.5"
    if (parts[0].length > 1 && parts[0].startsWith("0")) {
      parts[0] = parts[0].replace(/^0+/, "") || "0";
      inputValue = parts.join(".");
    }

    // Limit to 2 decimal places (industry standard: SAP, Oracle HCM, ADP)
    if (parts[1] !== undefined && parts[1].length > 2) return;

    const numValue = parseFloat(inputValue);

    if (isNaN(numValue)) {
      setter("");
      return;
    }

    // Block values below min
    if (numValue < min) return;

    if (enforcePercentageLimit && type === "Percentage" && numValue > 100) {
      setter("100");
      return;
    }

    setter(inputValue);
  };

  const renderValueInputs = () => {
    if (!payer) return null;

    const sharedBoxProps =
      payer === "Both"
        ? { width: "50%", ml: "5px" }
        : { width: "50%", ml: "5px" };

    const capInput = (
      <Box sx={{ ...style.boxs2, ...sharedBoxProps }}>
        <InputLabel sx={style.label}>Cap Amount</InputLabel>
        <TextField
          name="maxCap"
          value={cap}
          onChange={handleNumericChange(setCap, { min: 1 })}
          onBlur={() => {
            if (cap === ".") setCap("");
          }}
          placeholder="Enter cap amount"
          inputProps={{ inputMode: "decimal" }}
          sx={{ width: "100%" }}
        />
      </Box>
    );

    const descriptionInput = (
      <Box sx={{ ...style.boxs2, width: "100%" }}>
        <InputLabel sx={style.label}>Description</InputLabel>
        <TextField
          name="desc"
          value={description}
          required
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="Enter description"
          multiline
          minRows={3}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              padding: 0,
            },
            "& .MuiOutlinedInput-inputMultiline": {
              padding: 0,
            },
            "& textarea": {
              padding: 0,
            },
          }}
        />
      </Box>
    );

    const employerInput = (
      <Box sx={{ ...style.boxs2, ...sharedBoxProps }}>
        <InputLabel sx={style.label}>Employer Value</InputLabel>
        <TextField
          name="employerValue"
          value={employerValue}
          onChange={handleNumericChange(setEmployerValue, {
            enforcePercentageLimit: true,
          })}
          onBlur={() => {
            if (employerValue === "." || employerValue === "0.") setEmployerValue("0");
          }}
          placeholder="Enter employer value"
          inputProps={{ inputMode: "decimal" }}
          sx={{ width: "100%" }}
        />
      </Box>
    );

    const employeeInput = (
      <Box sx={{ ...style.boxs2, ...sharedBoxProps }}>
        <InputLabel sx={style.label}>Employee Value</InputLabel>
        <TextField
          name="employeeValue"
          value={employeeValue}
          onChange={handleNumericChange(setEmployeeValue, {
            enforcePercentageLimit: true,
          })}
          onBlur={() => {
            if (employeeValue === "." || employeeValue === "0.") setEmployeeValue("0");
          }}
          placeholder="Enter employee value"
          inputProps={{ inputMode: "decimal" }}
          sx={{ width: "100%" }}
        />
      </Box>
    );

    if (payer === "Both") {
      return (
        <>
          <Box sx={style.modalGrid}>
            {employerInput}
            {employeeInput}
          </Box>
          <Box sx={style.modalGrid}>
            {capInput}
            <Box sx={style.boxs2} />
          </Box>
          <Box sx={style.modalGrid}>{descriptionInput}</Box>
        </>
      );
    }

    if (payer === "Employer") {
      return (
        <>
          <Box sx={style.modalGrid} justifyContent="flex-start">
            {employerInput}
            {capInput}
          </Box>
          <Box sx={style.modalGrid}>{descriptionInput}</Box>
        </>
      );
    }

    if (payer === "Employee") {
      return (
        <>
          <Box sx={style.modalGrid} justifyContent="flex-start">
            {employeeInput}
            {capInput}
          </Box>
          <Box sx={style.modalGrid}>{descriptionInput}</Box>
        </>
      );
    }

    return null;
  };

  const formatValueForTable = (item) => {
    if (item?.paidBy === "Both") {
      const employerVal =
        item?.employerValue ??
        (typeof item?.value === "object" ? item?.value?.employer : item?.value);
      const employeeVal =
        item?.employeeValue ??
        (typeof item?.value === "object" ? item?.value?.employee : item?.value);
      return `Employer: ${employerVal ?? "-"} | Employee: ${employeeVal ?? "-"}`;
    }

    if (item?.paidBy === "Employer") {
      return item?.employerValue ?? item?.value ?? "-";
    }

    return item?.employeeValue ?? item?.value ?? "-";
  };

  const handleSubmit = () => {
    const payload = buildTaxRulePayload();
    if (isEdit) {
      handleEditTaxRule(id, payload);
    } else {
      handleAddTaxRule(payload);
    }
    setOpen(false);
  };

  const handleDeleteItem = () => {
    deleteTaxRule(value);
    setOpens(false);
  };

  const deleteData = (item) => {
    setValue(item);
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setOpen(true);
    setID(item._id);
    setPayer(item?.paidBy);
    setType(item?.type);
    setPayType(item?.payType || "");
    setDateFrom(dayjs(item?.startDate));
    setDateTo(dayjs(item?.endDate));
    const employerVal =
      item?.employerValue ??
      (typeof item?.value === "object"
        ? item?.value?.employer
        : item?.paidBy === "Employer" || item?.paidBy === "Both"
          ? item?.value
          : "");
    const employeeVal =
      item?.employeeValue ??
      (typeof item?.value === "object"
        ? item?.value?.employee
        : item?.paidBy === "Employee" || item?.paidBy === "Both"
          ? item?.value
          : "");
    setEmployerValue(
      employerVal === undefined || employerVal === null ? "" : String(employerVal)
    );
    setEmployeeValue(
      employeeVal === undefined || employeeVal === null ? "" : String(employeeVal)
    );
    setDescription(item?.description);
    setCap(item?.maxCap !== undefined && item?.maxCap !== null ? item.maxCap : "");
    if (typeof item?.forAllEmp === "boolean") {
      setApplyTo(item.forAllEmp ? "All Employees" : "Specific Employees");
    } else if (item?.applyTo) {
      setApplyTo(item.applyTo);
    } else {
      setApplyTo("");
    }

    // Store original values for comparison
    setOriginalValues({
      paidBy: item?.paidBy,
      type: item?.type,
      payType: item?.payType || "",
      startDate: item?.startDate,
      endDate: item?.endDate,
      forAllEmp: typeof item?.forAllEmp === "boolean" ? item.forAllEmp : false,
      employerValue: employerVal === undefined || employerVal === null ? null : Number(employerVal),
      employeeValue: employeeVal === undefined || employeeVal === null ? null : Number(employeeVal),
      description: item?.description || "",
      maxCap: item?.maxCap !== undefined && item?.maxCap !== null ? Number(item.maxCap) : null,
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
          mt: 3,
          ml: 3,
          mr: 2,
        }}
      >
        <Typography sx={style.gridText}>{`${label?.title} table`}</Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {canEditTaxTable && (
            <DeleteOutlineIcon
              sx={{ color: "#ff0000", cursor: "pointer" }}
              onClick={handleDeleteTaxRule}
            />
          )}
          {canEditTaxTable && (
            <Button
              variant="contained"
              type="submit"
              color="success"
              sx={style.jobButtons}
              onClick={() => {
                setOpen(true);
                setIsEdit(false);
                setPayer("");
                setDateFrom(null);
                setDateTo(null);
                setDescription("");
                setType("");
                setPayType("");
                setCap("");
                setApplyTo("");
                setEmployerValue("");
                setEmployeeValue("");
                setOriginalValues(null);
              }}
            >
              + Add
            </Button>
          )}
        </Box>
      </Box>
      <Grid container sx={style.mainContainer}>
        <Grid item xs={12} lg={12} sx={style.gridbox}>
          <Typography sx={style.gridTexttable}>{country}</Typography>

          <TableContainer sx={style.noShadow}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.tableHead}>Payer</TableCell>
                  <TableCell sx={style.tableHead}>Tax Type</TableCell>
                  <TableCell sx={style.tableHead}>Pay Type</TableCell>
                  <TableCell sx={style.tableHead}>Employee Rate</TableCell>
                  <TableCell sx={style.tableHead}>Employer Rate</TableCell>
                  <TableCell sx={style.tableHead}>Start Date</TableCell>
                  <TableCell sx={style.tableHead}>End Date</TableCell>
                  {canEditTaxTable && (
                    <TableCell sx={style.TableHead}>Action</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  Array.isArray(data) &&
                  data?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={style.tableData}>{item?.paidBy}</TableCell>
                      <TableCell sx={style.tableData}>{item?.type}</TableCell>
                      <TableCell sx={style.tableData}>
                        {item?.payType ? item.payType : "-"}
                      </TableCell>
                      <TableCell sx={style.tableData}>
                        {item?.employeeValue !== undefined && item?.employeeValue !== null ? item.employeeValue : (typeof item?.value === "object" ? item?.value?.employee ?? 0 : (item?.paidBy === "Employee" || item?.paidBy === "Both" ? (item?.value ?? 0) : 0))}
                      </TableCell>
                      <TableCell sx={style.tableData}>
                        {item?.employerValue !== undefined && item?.employerValue !== null ? item.employerValue : (typeof item?.value === "object" ? item?.value?.employer ?? 0 : (item?.paidBy === "Employer" || item?.paidBy === "Both" ? (item?.value ?? 0) : 0))}
                      </TableCell>
                      <TableCell sx={style.tableData}>
                        {formatDate(item?.startDate)}
                      </TableCell>
                      <TableCell sx={style.tableData}>
                        {formatDate(item?.endDate)}
                      </TableCell>
                      {canEditTaxTable && (
                        <TableCell sx={style.tabledata}>
                          <Box sx={style.tableActiondata}>
                            <Edit
                              onClick={() => handleEdit(item)}
                              style={{ cursor: "pointer", margin: 3 }}
                            />
                            <DeleteOutlineIcon
                              sx={{ color: "#ff0000", cursor: "pointer" }}
                              onClick={() => {
                                deleteData(item?._id), setOpens(true);
                              }}
                            />
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <CustomPagination
              pageCount={totalPage}
              totalItems={totalCount}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <Modal
        open={open}
        setOpen={setOpen}
        disabled={
          !payer ||
          !type ||
          !payType ||
          !datefrom ||
          !dateTo ||
          !valueIsValid ||
          !applyTo ||
          (isEdit && !hasChanges)
        }
        btnText={isEdit ? `Update ${label?.title}` : `Add ${label?.title}`}
        title={label?.title}
        handleCheck={handleSubmit}
      >
        <Box sx={style.modalGrid}>
          <Box sx={style.boxs2}>
            <InputLabel sx={style.label}>Paid By</InputLabel>
            <TextField
              select
              name="desc"
              value={payer || ""}
              onChange={(e) => setPayer(e.target.value)}
              style={style.boxs2}
              placeholder="Select payer"
              SelectProps={{
                displayEmpty: true,
              }}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              <MenuItem value="Employer">Employer</MenuItem>
              <MenuItem value="Employee">Employee</MenuItem>
              <MenuItem value="Both">Both</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ ...style.boxs2, ml: "5px" }}>
            <InputLabel sx={style.label}>Apply To</InputLabel>
            <TextField
              select
              name="applyTo"
              value={applyTo}
              onChange={(e) => setApplyTo(e.target.value)}
              sx={{ width: "100%" }}
              placeholder="Select option"
              SelectProps={{
                displayEmpty: true,
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              <MenuItem value="All Employees">All Employees</MenuItem>
              <MenuItem value="Specific Employees">Specific Employees</MenuItem>
            </TextField>
          </Box>

        </Box>
        <Box sx={style.modalGrid}>
          <Box sx={{ ...style.boxs2, width: "50%", mr: "5px" }}>
            <Typography sx={style.label}>Type</Typography>
            <TextField
              select
              name="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={style.boxs2}
              placeholder="Select type"
              SelectProps={{
                displayEmpty: true,
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              <MenuItem value="Percentage">Percentage</MenuItem>
              <MenuItem value="Amount">Amount</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ ...style.boxs2, width: "50%", ml: "5px" }}>
            <InputLabel sx={style.label}>Pay Type</InputLabel>
            <TextField
              select
              name="payType"
              value={payType}
              onChange={(e) => setPayType(e.target.value)}
              sx={{ width: "100%" }}
              placeholder="Select pay type"
              SelectProps={{
                displayEmpty: true,
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              <MenuItem value="Salary">Salary</MenuItem>
              <MenuItem value="Gross">Gross</MenuItem>
            </TextField>
          </Box>
        </Box>
        <Box sx={style.modalGrid}>
          <Box sx={{ ...style.boxs2, width: "50%", mr: "5px" }}>
            <Typography sx={style.inputTexts}>Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <DatePicker
                  value={datefrom}
                  maxDate={dateTo}
                  slotProps={{
                    textField: {
                      placeholder: "Select start date",
                    },
                    field: {
                      readOnly: true,
                    },
                  }}
                  sx={style.calenderInput}
                  onChange={(newValue) => {
                    setDateFrom(newValue);
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ ...style.boxs2, width: "50%", ml: "5px" }}>
            <Typography sx={style.inputTexts}>End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateTo}
                slotProps={{
                  textField: {
                    placeholder: "Select end date",
                  },
                  field: {
                    readOnly: true,
                  },
                }}
                minDate={datefrom}
                sx={style.calenderInput}
                onChange={(newValue) => {
                  setDateTo(newValue);
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        {renderValueInputs()}
      </Modal>

      <Modal
        open={opens}
        setOpen={setOpens}
        deletetText={"Delete"}
        cancelText={"Cancel"}
        title={"Delete"}
        handleDelete={handleDeleteItem}
      >
        <Box>
          <Typography>Are you sure you want to Delete?</Typography>
        </Box>
      </Modal>
    </>
  );
};

export default UniversalTaxPage;
